import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { auditApi } from "@/features/settings/api";
import { cn } from "@/lib/utils";

const KEY = ["audit"] as const;

function statusCls(code?: number | null) {
  if (!code) return "border-input text-muted-foreground";
  if (code >= 200 && code < 300)
    return "border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400";
  if (code >= 400)
    return "border-rose-200 text-rose-700 dark:border-rose-900 dark:text-rose-400";
  return "border-amber-200 text-amber-700 dark:border-amber-900 dark:text-amber-400";
}

export default function AuditLogTab() {
  const qc = useQueryClient();
  const [action, setAction] = useState<string>("all");

  const logsQ = useQuery({
    queryKey: [...KEY, "logs", action],
    queryFn: () =>
      auditApi.list(action === "all" ? undefined : { action }),
    staleTime: 15_000,
  });
  const typesQ = useQuery({
    queryKey: [...KEY, "action-types"],
    queryFn: auditApi.actionTypes,
    staleTime: 5 * 60_000,
  });

  const logs = logsQ.data ?? [];
  const types = typesQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {types.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            qc.invalidateQueries({ queryKey: [...KEY, "logs", action] })
          }
        >
          <RefreshCw className="size-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {logsQ.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          )}
          {!logsQ.isLoading && logs.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No audit entries found.
            </p>
          )}
          {!logsQ.isLoading && logs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Time</th>
                    <th className="px-4 py-2.5 font-medium">User</th>
                    <th className="px-4 py-2.5 font-medium">Action</th>
                    <th className="px-4 py-2.5 font-medium">Target</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-accent/40">
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {log.actor_email || (
                          <em className="text-muted-foreground">system</em>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                          {log.action}
                        </code>
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {log.target_type}
                        {log.target_id && (
                          <span className="ml-1 font-mono text-[10px]">
                            {log.target_id.slice(0, 8)}…
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {log.status_code != null && (
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs",
                              statusCls(log.status_code),
                            )}
                          >
                            {log.status_code}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {log.ip_address || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
